import { z } from "zod";

/**
 * Pre-FBA: Lieferungen an Amazon-Lager (Fulfillment Center). Amazon verlangt je Karton ein Kartonetikett (aus
 * Seller Central, „Send to Amazon“) und bei Paletten zusätzlich Palettenetiketten; einzelne Artikel müssen vorher mit
 * ihrer FNSKU beklebt werden (Druck im WMS von Logiful – Connect speichert die FNSKU und gibt sie ans Lager).
 *
 * Zwei Wege:
 * - seller_cartons: Kunde plant die Kartons selbst, legt die Sendung in Seller Central an und lädt die Etiketten hoch.
 *   Erst dann geht der Auftrag ans Lager – mit allen Etiketten als Anhang.
 * - warehouse_packs: Logiful kommissioniert, beklebt und packt zuerst und erfasst die Kartons (Inhalt, Gewicht, Maße)
 *   in Connect; damit erstellt der Kunde die Sendung in Seller Central und lädt die Etiketten hoch. Versand erst danach.
 */
export const FBA_PACKING = ["seller_cartons", "warehouse_packs"] as const;
export type FbaPacking = (typeof FBA_PACKING)[number];
export const FBA_PACKING_LABEL: Record<FbaPacking, string> = { seller_cartons: "Kunde gibt Kartons vor", warehouse_packs: "Logiful packt und meldet Kartons" };

export const FBA_DOCUMENT_KINDS = ["carton_labels", "pallet_labels", "other"] as const;
export type FbaDocumentKind = (typeof FBA_DOCUMENT_KINDS)[number];
export const FBA_DOCUMENT_LABEL: Record<FbaDocumentKind, string> = { carton_labels: "Kartonetiketten", pallet_labels: "Palettenetiketten", other: "Sonstiges" };

/** Amazon-Sendungsnummer, z. B. FBA15K2ZX9QW */
export const FBA_SHIPMENT_ID = /^FBA[0-9A-Z]{6,12}$/;
/** FNSKU (Amazon-Etikett), z. B. X001ABCDEF – oder ASIN bei herstellereigenem Barcode */
export const FNSKU_RE = /^[A-Z0-9]{10}$/;

export const productFbaSchema = z.object({
  fnsku: z
    .string()
    .trim()
    .toUpperCase()
    .nullable()
    .default(null)
    .transform((v) => v || null)
    .refine((v) => !v || FNSKU_RE.test(v), "FNSKU: 10 Zeichen, Buchstaben und Ziffern (z. B. X001ABCDEF)"),
  /** Artikel muss vor dem Versand an Amazon mit dem FNSKU-Etikett beklebt werden */
  labelRequired: z.boolean().default(false),
});
export type ProductFba = z.infer<typeof productFbaSchema>;

export const fbaCartonSchema = z.object({
  lines: z.array(z.object({ sku: z.string().min(1).max(80), quantity: z.number().int().min(1).max(100_000) })).min(1).max(100),
  weightKg: z.number().positive().max(1000),
  lengthCm: z.number().positive().max(300),
  widthCm: z.number().positive().max(300),
  heightCm: z.number().positive().max(300),
});
export type FbaCarton = z.infer<typeof fbaCartonSchema>;

/** Momentaufnahme am Auftrag (orders.fba) */
export interface FbaOrderInfo {
  /** Amazon-Sendungsnummer (bei „Logiful packt“ erst nach dem Packen bekannt) */
  shipmentId: string | null;
  /** Amazon-Referenz für den Spediteur (optional) */
  referenceId: string | null;
  /** Ziel-Fulfillment-Center, z. B. DTM2 */
  destinationCode: string;
  packing: FbaPacking;
  palletized: boolean;
  /** Artikel, die mit FNSKU beklebt werden müssen */
  fnskuLines: { sku: string; fnsku: string; quantity: number }[];
  /** von Logiful erfasste Kartons (Weg „Logiful packt“) */
  packedCartons: FbaCarton[] | null;
  packedAt: string | null;
}

/** Sperrgrund, solange die Etiketten fehlen (siehe holds.ts: Art „fba_labels“) */
export const FBA_HOLD_PREFIX = "Amazon-Etiketten fehlen: ";

/** Welche Etiketten fehlen noch? (leer = vollständig) */
export function fbaMissingLabels(info: Pick<FbaOrderInfo, "palletized">, uploaded: FbaDocumentKind[]): FbaDocumentKind[] {
  const need: FbaDocumentKind[] = ["carton_labels", ...(info.palletized ? (["pallet_labels"] as const) : [])];
  return need.filter((k) => !uploaded.includes(k));
}
export const fbaHoldReason = (missing: FbaDocumentKind[]) => `${FBA_HOLD_PREFIX}${missing.map((k) => FBA_DOCUMENT_LABEL[k]).join(" und ")} in Connect hochladen`;

/** Gepackte Kartons gegen die Auftragsmengen prüfen – Abweichungen (leer = passt) */
export function fbaCartonMismatches(cartons: FbaCarton[], lines: { sku: string; quantity: number }[]): string[] {
  const packed = new Map<string, number>();
  for (const c of cartons) for (const l of c.lines) packed.set(l.sku, (packed.get(l.sku) ?? 0) + l.quantity);
  const want = new Map<string, number>();
  for (const l of lines) want.set(l.sku, (want.get(l.sku) ?? 0) + l.quantity);
  const out: string[] = [];
  for (const [sku, n] of want) if ((packed.get(sku) ?? 0) !== n) out.push(`${sku}: gepackt ${packed.get(sku) ?? 0}, bestellt ${n}`);
  for (const [sku, n] of packed) if (!want.has(sku)) out.push(`${sku}: ${n} gepackt, aber nicht in der Sendung`);
  return out;
}

/** Hinweis fürs Lager (FFN „interne Notiz“) – Ablauf, FNSKU-Bekleben, Etikettenstand */
export function fbaWarehouseNote(i: FbaOrderInfo, opts: { labelsComplete: boolean; note?: string | null }): string {
  const lines = [
    `PRE-FBA an Amazon ${i.destinationCode}${i.shipmentId ? ` · Sendung ${i.shipmentId}` : ""}${i.referenceId ? ` · Ref ${i.referenceId}` : ""}`,
    opts.labelsComplete
      ? `ETIKETTEN DA – versandbereit (Kartonetiketten${i.palletized ? " und Palettenetiketten" : ""} als Anhang)`
      : i.packing === "warehouse_packs"
        ? "Kommissionieren, bekleben und packen; Kartons (Inhalt, Gewicht, Maße) in Connect erfassen. NICHT VERSENDEN, bis die Amazon-Etiketten am Auftrag hängen."
        : "Wartet auf Amazon-Etiketten – nicht versenden.",
    ...(i.palletized ? ["Auf Palette(n) – je Palette ein Palettenetikett (4 Seiten), Kartonetiketten sichtbar"] : ["Einzelkartons (Paketversand) – je Karton ein Kartonetikett"]),
    ...(i.fnskuLines.length ? ["FNSKU bekleben (Herstellerbarcode abdecken):", ...i.fnskuLines.map((l) => `  ${l.sku} → ${l.fnsku} × ${l.quantity}`)] : []),
    ...(opts.note ? [`Notiz: ${opts.note}`] : []),
  ];
  return lines.join("\n");
}

/** Kartondaten als CSV (für „Send to Amazon“: Kartonanzahl, Inhalt, Gewicht, Maße) */
export function fbaCartonsCsv(cartons: FbaCarton[], fnskuOf: (sku: string) => string | null): string {
  const esc = (v: string | number) => (/[;"\n]/.test(String(v)) ? `"${String(v).replace(/"/g, '""')}"` : String(v));
  const rows = [["Karton", "SKU", "FNSKU", "Menge", "Gewicht kg", "Länge cm", "Breite cm", "Höhe cm"]];
  cartons.forEach((c, i) => c.lines.forEach((l) => rows.push([String(i + 1), l.sku, fnskuOf(l.sku) ?? "", String(l.quantity), String(c.weightKg).replace(".", ","), String(c.lengthCm), String(c.widthCm), String(c.heightCm)])));
  return rows.map((r) => r.map(esc).join(";")).join("\r\n") + "\r\n";
}
